// src/pages/AccountSettings.jsx
import React, { useState, useEffect } from "react";
import AccountSettingsHeader from "../components/widgets/AccountSettingsHeader";
import Header from "../components/Header";
import StickyFooter from "../components/StickyFooter";

import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  useTheme,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import PersonIcon from "@mui/icons-material/Person";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TagIcon from "@mui/icons-material/Tag";
import PhoneIcon from "@mui/icons-material/Phone";
import PublicIcon from "@mui/icons-material/Public";

import { getUser, updateProfile } from "../utils/api";

const AccountSettings = () => {
  const theme = useTheme();
  const [avatarUrl, setAvatarUrl] = useState(null);

  const [formData, setFormData] = useState({
    full_name: "",
    username: "",
    email: "",
    phone_number: "", // ✅ consistent with backend
    country: "",
    country_code: "",
    account_type: "",
    account_number: "",
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ✅ Load user on mount
  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const user = await getUser();
      setFormData({
        full_name: user.full_name || "",
        username: user.username || "",
        email: user.email || "",
        phone_number: user.phone_number || "",
        country: user.country || "",
        country_code: user.country_code || "",
        account_type: user.account_type || "",
        account_number: user.account_number || "",
      });
      setAvatarUrl(user.avatar_url || null);
    } catch (err) {
      console.error("Failed to load user:", err);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(formData.account_number);
    setSnackbar({
      open: true,
      message: "Account number copied!",
      severity: "success",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async () => {
    try {
      await updateProfile(formData);

      // 🔄 Force re-fetch fresh user from backend
      const updatedUser = await getUser(true);

      setFormData({
        full_name: updatedUser.full_name || "",
        username: updatedUser.username || "",
        email: updatedUser.email || "",
        phone_number: updatedUser.phone_number || "",
        country: updatedUser.country || "",
        country_code: updatedUser.country_code || "",
        account_type: updatedUser.account_type || "",
        account_number: updatedUser.account_number || "",
      });

      setAvatarUrl(updatedUser.avatar_url);

      setSnackbar({
        open: true,
        message: "Profile updated successfully!",
        severity: "success",
      });
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to update profile.",
        severity: "error",
      });
    }
  };

  // ✅ Add this missing function
  const handleCloseSnackbar = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        bgcolor: theme.palette.background.default,
        color: theme.palette.text.primary,
        overflowX: "hidden",
        fontFamily: "Poppins, Inter, Arial, sans-serif",
      }}
    >
      <Box p={{ xs: 2, md: 4 }} maxWidth={480} mx="auto">
        <Header />
        <AccountSettingsHeader avatarUrl={avatarUrl} setAvatarUrl={setAvatarUrl} />

        <Typography variant="h6" fontWeight={600} mb={1}>
          Profile Information
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3} mr={2}>
          Your personal information and account details
        </Typography>

        <Paper
          elevation={2}
          sx={{
            mr: 2,
            mb: 2,
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            backgroundColor: theme.palette.background.paper,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 0 20px #1e293b"
                : "0 0 10px #e2e8f0",
          }}
        >
          {/* Full Name */}
          <TextField
            fullWidth
            margin="normal"
            label="Full Name"
            name="full_name"
            value={formData.full_name}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Username */}
          <TextField
            fullWidth
            margin="normal"
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Email */}
          <TextField
            fullWidth
            margin="normal"
            label="Email Address"
            name="email"
            value={formData.email}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AlternateEmailIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Phone */}
          <TextField
            fullWidth
            margin="normal"
            label="Phone Number"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Country */}
          <TextField
            fullWidth
            margin="normal"
            label="Country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PublicIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Country Code */}
          <TextField
            fullWidth
            margin="normal"
            label="Country Code"
            name="country_code"
            value={formData.country_code}
            onChange={handleChange}
            InputProps={{
              startAdornment: <InputAdornment position="start">+</InputAdornment>,
            }}
          />

          {/* Account Type */}
          <FormControl fullWidth margin="normal">
            <InputLabel id="account-type-label">Account Type</InputLabel>
            <Select
              labelId="account-type-label"
              name="account_type"
              value={formData.account_type}
              onChange={handleChange}
            >
              <MenuItem value="main">Main</MenuItem>
              <MenuItem value="savings">Savings</MenuItem>
              <MenuItem value="current">Current</MenuItem>
            </Select>
          </FormControl>

          {/* Account Number (read-only) */}
          <TextField
            fullWidth
            margin="normal"
            label="Account Number"
            name="account_number"
            value={formData.account_number}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <TagIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleCopy}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Update Button */}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 3 }}
            onClick={handleUpdate}
          >
            Update Details
          </Button>
        </Paper>
      </Box>

      <StickyFooter />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountSettings;